// @ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import { Map, Source, Layer, Popup } from "react-map-gl";
import * as turf from '@turf/turf';
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setFlyLocation } from "../../store/mapStateSlice";
import { setModalState, modalViewsConst } from "../../store/modalSlice";
import useIsMobile from "../../hooks/useIsMobile";
import { ReactComponent as MapIcon } from "../../assets/icons/map-02.svg";
import { ReactComponent as GlobeIcon } from "../../assets/icons/globe-02.svg";
import "mapbox-gl/dist/mapbox-gl.css";
import { getTHEFW, getOHHFC, getTHEFWwind80, getGraphCast, interpolateCurveValues } from "../../api/awsLamba";
import { getForecastPath } from "./utils/forecastUtils";
import { list, downloadData, getUrl } from "@aws-amplify/storage";
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import Collapse from '@mui/material/Collapse';
import { FaPlay, FaPause, FaStepForward, FaUndo, FaInfoCircle, FaTags, FaChevronUp, FaChevronDown, FaEye, FaEyeSlash, FaCog } from 'react-icons/fa';
import { getColorForValue } from './utils/colorUtils';
import { getForecastUnit, findClosestTimeIndex } from './utils/forecastUtils';
import { formatDateTimeLookUp } from './utils/dateUtils';
import { createPointFeature, createAssetFeature } from './utils/mapUtils';
import { loadAssetIcons, getIconImageExpression, hasLoadedAssetIcons } from './utils/iconUtils';
import { FORECAST_VARIATIONS, HUMIDITY_CHANNEL_OPTIONS, WIND_POWER_CHANNEL_OPTIONS, PRECIPITATION_CHANNEL_OPTIONS, SOLAR_LONG_CHANNEL_OPTIONS, SOLAR_SHORT_CHANNEL_OPTIONS, TEMPERATURE_CHANNEL_OPTIONS, WIND_80_CHANNEL_OPTIONS, WIND_10_CHANNEL_OPTIONS, SNOW_WATER_CHANNEL_OPTIONS, SOIL_MOISTURE_CHANNEL_OPTIONS } from './utils/constants';
import { mapCursorStyle, MAPBOX_TOKEN } from './utils/mapStyles';
import { extractProcessedModelChannelsWithMax, extractTimeRangeFromProcessedModel } from './utils/processedModelUtils';
import LoadingOverlay from './components/LoadingOverlay';
import ForecastLegend from './components/ForecastLegend';
import MapPopups from './components/MapPopups';
import MapStyleToggle from './components/MapStyleToggle';
import ForecastControls from './components/ForecastControls';
import MobileDrawer from './components/MobileDrawer';
import MobileControlPanel from './components/MobileControlPanel';
import { formatPowerEnsembleToPercentile, timePowerDataToPercentile } from "./utils/formattingUtils";
import { useImagePreloader } from "../../hooks/useImagePreloader";
import { withTrailingSlash } from "../../utils/s3Path";
import { isMapViewForecastDisplayed } from "../utils/forecastSchemaUtils";

const AssetMapView = ({ assetItems, assetGroup, isEmbedded = false, showWeatherData = true, showOnlyAssetData = false, onAssetClick = null }) => {
  const mapRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [mapStyle, setMapStyle] = useState("mapbox://styles/mapbox/light-v9");
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Preload asset images for better caching
  useImagePreloader(assetItems);
  
  // Forecast data state
  // Forecast state variables
  const [forecastData, setForecastData] = useState({});
  // Track which forecast types are already loaded
  const [loadedForecasts, setLoadedForecasts] = useState({
    GEFS25: false, // For THEFW data
    HRRR: false, // For OHHFC data
    GEFS50: false, // For THEFW wind80 data
    GRAPHCAST: false // For Graphcast data
  });
  const [timeRange, setTimeRange] = useState([]);
  const [sliderValue, setSliderValue] = useState(0);
  
  // Selection state
  const [forecastType, setForecastType] = useState('temperature');
  const [forecastChannel, setForecastChannel] = useState('hrrr-Hourly'); // Empty initially, no auto-selection
  const [forecastVariation, setForecastVariation] = useState(''); // Empty initially
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [channelOptions, setChannelOptions] = useState(HUMIDITY_CHANNEL_OPTIONS);
  const [variationOptions, setVariationOptions] = useState([]);
  const [dataFetched, setDataFetched] = useState(false); // Tracks if data has been fetched
  const [isPlaying, setIsPlaying] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState(100); // milliseconds between frames
  const animationRef = useRef(null);
  const [viewState, setViewState] = useState({
    longitude: -103.037,
    latitude: 53.51,
    zoom: 3
  });
  
  // State for tracking hovered feature and tooltip visibility
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [showAllTooltips, setShowAllTooltips] = useState(false);
  
  // Mobile UI state
  const [controlsExpanded, setControlsExpanded] = useState(!isMobile);
  const [controlsVisible, setControlsVisible] = useState(!isEmbedded);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapReady, setMapReady] = useState(!isEmbedded);
  const [iconsVersion, setIconsVersion] = useState(0);

  // Defer map mount for embedded maps so the container is laid out first
  // (prevents Mapbox unproject crash when projection matrix isn't ready)
  useEffect(() => {
    if (!mapReady) {
      requestAnimationFrame(() => setMapReady(true));
    }
  }, []);

  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    };
  }, []);

  // Processed Models state
  const [processedModelData, setProcessedModelData] = useState(null);
  const [processedModelsLoading, setProcessedModelsLoading] = useState(false);
  // const [processedModelOptions, setProcessedModelOptions] = useState([]);
  const [processedModelDetails, setProcessedModelDetails] = useState([]);
  const [localTimeBool, setUseLocalTime] = useState(true); // Default to local time
  
  // State for wind power calculations
  const [windPowerData, setWindPowerData] = useState({});
  // Flag to show wind power option in menu - true if any assets have wind curves
  const [showWindPowerOption, setShowWindPowerOption] = useState(false);
  // Store max wind power value for dynamic scaling
  const [maxWindPower, setMaxWindPower] = useState(1); // Default fallback value
  
  // Check if any assets have wind curves for showing wind power option
  useEffect(() => {
    if (assetItems && assetItems.length > 0) {
      // Check if any assets have wind curves
      let hasWindCurves = false;
      let tempMaxWindPower = 1;
      assetItems.forEach(asset =>{
        if(asset.windCurve && Array.isArray(asset.windCurve.curveData) && asset.windCurve.curveData.length > 0) {
          hasWindCurves = true
          asset.windCurve.curveData.forEach(curvePoint => {
            tempMaxWindPower = Math.max(curvePoint?.y, tempMaxWindPower)
          });
          }
      });
      // divisible by 4 for Legend
      const ceilingWindPower = Math.ceil(tempMaxWindPower / 4) * 4
      setMaxWindPower(ceilingWindPower)

      // Assets with wind curves check
      setShowWindPowerOption(hasWindCurves);
    }
  }, [assetItems]);

  // Check for processed Model channels
  useEffect(() => {
    let isMounted = true;
    
    //NOTE Craig refactor this
    const checkAssetProperties = async () => { 
      if (!assetItems || assetItems.length === 0) {      
        return;
      }
      
      setLoading(true);
      
      try {
        // Extract all unique processed model routeShort values from all assets
        const processedModels = [];
        assetItems.forEach(asset => {
          if (asset.processedModels?.items?.length > 0) {
            asset.processedModels.items.forEach(model => {
              // if (!processedModels.some(pm => pm.routeShort === model.routeShort)) {
                processedModels.push({
                  routeShort: model.routeShort,
                  label: model.label || model.routeShort,
                  routeLong: model.routeLong,
                  assetId: asset.id
                });
            });
          }
        });

        // Store the processed model options
        setProcessedModelDetails(processedModels); //Craig maybe remove this
        
        // Fetch directory.json files and update processed model details
        if(processedModels.length > 0) {
          fetchDirectoryJsonFiles(processedModels);
        }
        // Don't fetch all forecasts immediately, we'll load them on-demand
        setProcessedModelsLoading(false);
        
        // Initialize empty forecast data structure for all assets
        const emptyForecasts = assetItems
          .filter(asset => asset.lat && asset.lng)
          .reduce((acc, asset) => {
            acc[asset.id] = {
              thefw: null,
              ohhfc: null,
              wind80: null,
              graphcast: null
            };
            return acc;
          }, {});
        
        setForecastData(emptyForecasts);
        
        // Initialize with empty time range
        setTimeRange([]);

      } catch (error) {
        console.error('Error initializing asset properties:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    checkAssetProperties();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [assetItems]);
  
  // Function to load all forecasts at once on component initialization

  // Function to load forecast data on demand based on selected forecast type and channel
  // This optimizes network requests by only loading the specific forecast source needed
  // isProcessedModel flag allows loading processed models without requiring a channel
  const loadForecastData = async (selectedType, selectedChannel, isProcessedModel = false) => {
    // For regular (non-processed) models, we need both type and channel
    // For processed models, we only need the type to begin loading
    if (!selectedType || (!selectedChannel && !isProcessedModel)) return;
    // Determine which forecast source we need
    let targetForecastSource = null;
    // Handle processed models differently
    if (selectedType.startsWith('processed-')) {
      // For processed models, we'll use the models from processedModelDetails
      // This is handled in getProcessedModelS3() function which is called from handleForecastTypeChange
      // No need to set a targetForecastSource or continue with regular forecast loading
      return;
    } else if (selectedChannel.startsWith('hrrr-')) {
      targetForecastSource = 'HRRR';
    } else if (selectedChannel.startsWith('gefs50-')) {
      targetForecastSource = 'GEFS50';
    } else if (selectedType === 'graphcast-wind' || selectedType === 'graphcast-temperature') {
      targetForecastSource = 'GRAPHCAST';
    } else {
      targetForecastSource = 'GEFS25';
    }
    
    // Check if this forecast source is already loaded
    if (loadedForecasts[targetForecastSource]) {
      console.log(`${targetForecastSource} data already loaded, using cached data`);

      let firstAssetId
      if(selectedType === "windPower"){
        const firstAsset = assetItems.find(asset=>asset.windCurve)
        firstAssetId = firstAsset.id
      } else {
        firstAssetId = Object.keys(forecastData)[0];
      }
      // Set time range based on existing data
   
      if (firstAssetId && forecastData[firstAssetId]) {
        const timeData = getForecastPath(selectedType, forecastVariation, selectedChannel, forecastData[firstAssetId], assetItems.find(asset=>asset.id === firstAssetId) )
           
        if (timeData && timeData.length > 0) {
          setTimeRange(timeData.map(item => new Date(item.x)));
          setSliderValue(0);
        }
      }

      return;
    }
  
    setLoading(true);
    let forecastLoadList = []
    try {
      const forecastPromises = assetItems
        .filter(asset => asset.lat && asset.lng)
        .map(async (asset) => {
          // Only fetch the data we need based on the forecast source
          let thefwData = null;
          let ohhfcData = null;
          let wind80Data = null;
          let graphcastData = null;

          // Lazy load: only fetch the specific forecast source needed
          if (targetForecastSource === 'GEFS25' && !loadedForecasts['GEFS25']) {
            thefwData = await getTHEFW(asset.lat, asset.lng);
            forecastLoadList.push('GEFS25')
          } else if (targetForecastSource === 'HRRR' && !loadedForecasts['HRRR']) {
            ohhfcData = await getOHHFC(asset.lat, asset.lng);
            forecastLoadList.push('HRRR')
          } else if (targetForecastSource === 'GEFS50' && !loadedForecasts['GEFS50']) {
            wind80Data = await getTHEFWwind80(asset.lat, asset.lng);
            forecastLoadList.push('GEFS50')
          } else if (targetForecastSource === 'GRAPHCAST' && !loadedForecasts['GRAPHCAST']) {
            graphcastData = await getGraphCast(asset.lat, asset.lng);
            forecastLoadList.push('GRAPHCAST')
          }

          // Check for wind power capability
          if (asset.windCurve && asset.windCurve.curveData && ohhfcData?.wind?.wind80?.Speed) {
            const sortedHRRRCurveData = [...asset.windCurve.curveData].sort(
                (a, b) => a.x - b.x
              );
            let windPower = interpolateCurveValues(sortedHRRRCurveData, ohhfcData?.wind?.wind80?.Speed)
            ohhfcData["windPower"] = windPower
          } 
          if (asset.windCurve && asset.windCurve.curveData && wind80Data?.isoKeyMagEnsemble) {
            try {

            const sortedTHEFWCurveData = [...asset.windCurve.curveData].sort(
                (a, b) => a.x - b.x
              );
            const timePowerData =  formatPowerEnsembleToPercentile(wind80Data.isoKeyMagEnsembleInterpolate, sortedTHEFWCurveData)    
            const percentileWindPowerData = timePowerDataToPercentile(timePowerData)
            wind80Data['windPower'] = percentileWindPowerData 
            } catch (e) {
              console.log("error", e)
            }
          }      

          return {
            assetId: asset.id,
            forecasts: {
              thefw: thefwData,
              ohhfc: ohhfcData,
              wind80: wind80Data,
              graphcast: graphcastData
            }
          };
        });
      
      // Wait for all forecast promises to resolve
      const results = await Promise.all(forecastPromises);
      const newData = { ...forecastData };

      results.forEach(({ assetId, forecasts }, i) => {
        newData[assetId] = newData[assetId] || {};
        // Only update the forecast types that were loaded
        if (forecasts.thefw) newData[assetId].thefw = forecasts.thefw;
        if (forecasts.ohhfc) newData[assetId].ohhfc = forecasts.ohhfc;
        if (forecasts.wind80) newData[assetId].wind80 = forecasts.wind80;
        if (forecasts.graphcast) newData[assetId].graphcast = forecasts.graphcast;
      });

      // Set the new data directly

      setForecastData(newData);

      //setSlider based off data, THIS COULD CAUSE ISSUE - maybe get data from results?
      const firstAssetId = Object.keys(newData)[0];
      const timeData = getForecastPath(selectedType, forecastVariation, selectedChannel, newData[firstAssetId], assetItems.find(asset=>asset.id ===firstAssetId) )
      if (timeData && timeData.length > 0) {
        setTimeRange(timeData.map(item => new Date(item.x)));
        setSliderValue(0);
      }
      
      // Mark this forecast source as loaded
      // setLoadedForecasts(prev => ({
      //   ...prev,
      //   [targetForecastSource]: true
      // }));

      const uniquelyLoadedForecast = [...new Set(forecastLoadList)]


      setLoadedForecasts(prev => {
      return Object.fromEntries(
        Object.entries(prev).map(([key, wasTrue]) => [
          key,
          wasTrue || uniquelyLoadedForecast.includes(key)
        ])
      );
    });
      
    } catch (error) {
      console.error(`Error loading ${targetForecastSource} data:`, error);
    } finally {
      setLoading(false);
    }
  };

  // Load forecast data when forecast type and channel change
  useEffect(() => {
    // Skip loading forecast data if showWeatherData is false
    if (!showWeatherData) return;
    
    // For processed models, load data as soon as forecast type is selected (without waiting for channel)
    // For regular forecast types, wait until both type and channel are selected
    const isProcessedModel = forecastType && forecastType.startsWith('processed-');

    if ((isProcessedModel && forecastType) || (forecastType && forecastChannel)) {
      // For processed models, we'll load even without a channel
      // For regular forecasts, we need both type and channel

      loadForecastData(forecastType, forecastChannel, isProcessedModel);
      
    }
  }, [forecastType, forecastChannel, forecastVariation, forecastData, assetItems, showWeatherData]);
  
  // Handle hover on a feature
  const handleAssetHover = (e) => {
    // Don't show hover tooltip if all tooltips are displayed
    if (showAllTooltips) return;

    const features = e.features || [];
    if (features.length > 0) {
      const feature = features[0];
      const featureId = feature.properties.id;

      // Get fresh feature data from assetGeoJSON to ensure forecast values are current
      let freshFeature = feature;
      if (assetGeoJSON && assetGeoJSON.features) {
        const foundFeature = assetGeoJSON.features.find(f => f.properties.id === featureId);
        if (foundFeature) {
          freshFeature = foundFeature;
        }
      }

      // Only update if we're hovering over a different feature
      if (!hoveredFeature || hoveredFeature.feature?.properties?.id !== featureId) {
        setHoveredFeature({
          feature: freshFeature,
          longitude: e.lngLat.lng,
          latitude: e.lngLat.lat
        });
      }
    } else if (hoveredFeature) {
      // Clear hovered feature if mouse is no longer over any feature
      setHoveredFeature(null);
    }
  };

  // Handle mouse leave
  const handleAssetLeave = () => {
    setHoveredFeature(null);
  };

  // Handle click on a feature
  const handleAssetClick = (e) => {
    const features = e.features || [];
    if (features.length > 0) {
      const feature = features[0];
      const assetId = feature.properties.id;
      const assetGroupId = feature.properties.assetGroupId;
      
      if (assetId && assetGroupId) {
        const { lng, lat } = e.lngLat;
        dispatch(setFlyLocation([lng, lat]));

        if (onAssetClick) {
          onAssetClick(assetId, assetGroupId);
        } else {
          navigate(`/home/asset-page/${assetGroupId}/${assetId}`);
        }
      }
    }
  };


  const openAddAssetFromMapModal = (lat: number, lng: number) => {
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.confirmAddAssetFromMap,
      properties: {
        __modalSize: 'xs',
        assetGroupId: assetGroup?.id,
        lat,
        lng,
      },
    }));
  };

  // True when the given map feature is an existing asset marker
  const isAssetFeature = (feature) =>
    !!(feature?.properties?.id && feature?.properties?.assetGroupId);

  // True when the screen point sits on top of an existing asset marker, so we
  // don't offer "create asset here" over a marker the user means to click.
  const isPointOnAsset = (point) => {
    const map = mapRef.current?.getMap?.();
    if (!map || !point) return false;
    try {
      return map
        .queryRenderedFeatures(point, { layers: interactiveLayerIds })
        .some(isAssetFeature);
    } catch {
      return false;
    }
  };

  const handleMapContextMenu = (e) => {
    if (!assetGroup) return;
    e.originalEvent?.preventDefault();
    // Skip when right-clicking an existing asset marker
    if ((e.features || []).some(isAssetFeature)) return;
    const { lat, lng } = e.lngLat;
    openAddAssetFromMapModal(lat, lng);
  };

  const handleLongPressStart = (e) => {
    if (!mapRef.current || !assetGroup) return;
    if (!e.originalEvent?.touches?.[0]) return;
    // Skip when long-pressing an existing asset marker
    if (isPointOnAsset(e.point)) return;
    const { lat, lng } = e.lngLat;
    longPressTimerRef.current = setTimeout(() => {
      openAddAssetFromMapModal(lat, lng);
    }, 600);
  };

  const handleLongPressEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // Generate GeoJSON from asset data with forecast colors
  const generateAssetsGeoJSON = () => {
    if (!assetItems || assetItems.length === 0) return null;
    
    const features = assetItems.map((asset) => {
      // Determine color based on forecast data
      let color = '#A2BBBF'; // Default grey color if no forecast
      let forecastValue = null;
     
      // Get forecast value for the current asset at the selected time (only if timeRange exists)
      if (timeRange.length > 0) {
        const selectedTime = timeRange[sliderValue];
        // Check if this is a processed model - for processed models, the timeRange 
        // already contains the formatted data with asset values
        if (forecastType.startsWith('processed-')) {
          // Get the selected time as a Date object
          const selectedTimeObj = new Date(selectedTime);
          
          // Find max value for the selected channel (for color scaling)
          const selectedVariationChannel = channelOptions.find(channelOptn => channelOptn.value === forecastChannel);
          const maxValue = selectedVariationChannel?.maxValue || 5; // Default to 5 if not found

          // Check if we have data for this asset
          if (processedModelData?.[asset.id]?.models) {
            const availableModels = processedModelData?.[asset.id]?.models;
            // Find the correct model based on the selected variation
            let modelData = null;
            if (selectedVariationChannel?.variation) {
              // Look for a model whose variation field matches the selected variation
              const matchingModelKey = Object.keys(availableModels).find(modelKey =>
                availableModels[modelKey]?.variation === selectedVariationChannel.variation
              );
              if (matchingModelKey) {
                modelData = availableModels[matchingModelKey]?.data;
              }
            }

            // If no matching model found, use the first available model as fallback
            if (!modelData) {
              console.warn("No Model Data, fallback")
              const firstModelKey = Object.keys(availableModels)[0];
              modelData = availableModels[firstModelKey]?.data;
            }
            
            // Find the closest timestamp entry regardless of format
            if (modelData) {
              // Find the matching timestamp by comparing exact Date time values
              const matchingTimestamp = Object.keys(modelData).find(timestampStr => {
                const timestampDate = new Date(timestampStr);
                // Compare exact time values (milliseconds since epoch)
                return timestampDate.getTime() === selectedTimeObj.getTime();
              });
              
              if (matchingTimestamp && modelData[matchingTimestamp]) {
                // Get the forecast value for this timestamp and variation channel
                forecastValue = modelData[matchingTimestamp][selectedVariationChannel?.variationChannel];
                
                if (forecastValue !== undefined && forecastValue !== null) {
                  // Apply color based on the forecast value and max value
                  color = getColorForValue(forecastValue, 'processed', maxValue);
                }
              }
            }
          }
        }
        // Standard forecast data handling
        else if (forecastData[asset.id] && (
          forecastData[asset.id].thefw || 
          forecastData[asset.id].ohhfc || 
          forecastData[asset.id].graphcast ||
          forecastData[asset.id].wind80
        )) {
          // Get the appropriate forecast data based on the selected forecast type and channel
          let relevantForecast;

          try {
            relevantForecast = getForecastPath(
                forecastType, 
                forecastVariation, 
                forecastChannel,
                forecastData[asset.id],
                asset
              );
            
          } catch (error) {
            console.error(`Error accessing forecast data for asset ${asset.id}:`, error);
          }
        
          if (relevantForecast) {
            // Standard handling for array forecast data
            if (Array.isArray(relevantForecast)) {
              const closestIndex = findClosestTimeIndex(relevantForecast, selectedTime);
              if (closestIndex >= 0) {
                forecastValue = relevantForecast[closestIndex].y;
                
                // Check for invalid values
                if (forecastValue !== null && forecastValue !== undefined && !isNaN(forecastValue)) {
                  // Found forecast value for asset
                  color = getColorForValue(forecastValue, forecastType, Math.ceil(maxWindPower));
                }
              }
            } else {
              console.warn(`Forecast data for asset ${asset.id} is not an array:`, relevantForecast);
            }
          }
        }
      }
      
      // Use the extracted utility function to create the feature
      return createAssetFeature(asset, color, forecastValue);
    });

    return {
      type: "FeatureCollection",
      features: features.filter(Boolean)
    };
  };

  // Generate and memoize asset GeoJSON data when relevant state changes
  const assetGeoJSON = React.useMemo(() => {
    // Always generate GeoJSON if we have assets, even without forecast data
    if (!assetItems || assetItems.length === 0) return null;
    return generateAssetsGeoJSON();
  }, [assetItems, forecastData, forecastType, forecastChannel, forecastVariation, sliderValue, timeRange]);

  // When the map loads, fit bounds to show all assets
  const handleMapLoad = () => {
    if (!mapRef.current || !assetItems || assetItems.length === 0) return;

    // Load SDF icons for custom asset markers
    try {
      const mapInstance = mapRef.current.getMap();
      loadAssetIcons(mapInstance);
      setIconsVersion(v => v + 1);
      // Re-load icons after style changes (style swap wipes custom images)
      mapInstance.on("style.load", () => {
        loadAssetIcons(mapInstance);
        setIconsVersion(v => v + 1);
      });
    } catch (e) {
      console.error("[AssetIcons] Error loading asset icons:", e);
    }

    try {
      // Create features from all assets
      const features = [];
      
      // Process each asset into features for bounds calculation
      assetItems.forEach(asset => {
        if (asset.geojson) {
          try {
            const geojsonObj = JSON.parse(asset.geojson);
            
            // Check what type of GeoJSON we have
            if (geojsonObj.type === 'FeatureCollection' && geojsonObj.features) {
              // Add all features from the collection
              geojsonObj.features.forEach(feature => {
                if (feature.geometry) {
                  features.push(feature);
                }
              });
            } else if (geojsonObj.type === 'Feature' && geojsonObj.geometry) {
              // Add the single feature
              features.push(geojsonObj);
            } else if (geojsonObj.type && geojsonObj.coordinates) {
              // It's a raw geometry - wrap it in a feature
              features.push({
                type: 'Feature',
                geometry: geojsonObj,
                properties: {}
              });
            }
          } catch (e) {
            console.error("Error parsing geojson for asset:", asset.id, e);
            // Fallback to point if available
            if (asset.lat && asset.lng) {
              features.push(turf.point([asset.lng, asset.lat]));
            }
          }
        } else if (asset.lat && asset.lng) {
          // Create point feature for assets without GeoJSON
          features.push(turf.point([asset.lng, asset.lat]));
        }
      });
      
      if (features.length > 0) {
        const featureCollection = turf.featureCollection(features);
        const bounds = turf.bbox(featureCollection);
        
        // Check if bounds are valid (not just a single point or very small area)
        const isValidBounds = 
          Math.abs(bounds[0] - bounds[2]) > 0.00001 || 
          Math.abs(bounds[1] - bounds[3]) > 0.00001;
        
        if (isValidBounds) {
          const mapboxBounds = [[bounds[0], bounds[1]], [bounds[2], bounds[3]]];
          
          mapRef.current.fitBounds(mapboxBounds, {
            padding: isMobile ? 30 : 50,
            duration: 1000
          });
        }
      }
    } catch (e) {
      console.error("Error fitting bounds:", e);
    }

    setMapLoaded(true);
  };

  // Create the list of interactive layer IDs for the map
  // Only return IDs after the map has loaded to prevent Mapbox from querying
  // features before the projection matrix is ready (causes vec4 crash on dashboard).
  const interactiveLayerIds = React.useMemo(() => {
    if (!mapLoaded) return [];
    const ids = ['assets-fill', 'assets-line', 'assets-points', 'assets-hover-area'];
    // Only add icon layers if the map has the SDF images loaded
    if (mapRef.current) {
      const map = mapRef.current.getMap?.();
      if (hasLoadedAssetIcons(map)) {
        ids.push('assets-icons-fill', 'assets-icons-stroke');
      }
    }
    return ids;
  }, [mapLoaded, assetGeoJSON, mapStyle, iconsVersion]);
  
  // Handle slider change
  const handleSliderChange = (event, newValue) => {
    setSliderValue(newValue);
  };
  
  // Play/pause animation
  const togglePlayAnimation = () => {
    setIsPlaying(!isPlaying);
  };
  
  // Step forward one frame
  const stepForward = () => {
    // If at the end of the timeline, restart from beginning
    if (sliderValue >= timeRange.length - 1) {
      setSliderValue(0);
    } else {
      // Otherwise advance to next frame
      setSliderValue(sliderValue + 1);
    }
  };
  
  // Restart animation from beginning
  const restartSlider = () => {
    setSliderValue(0);
  };
  
  // Animation effect
  useEffect(() => {
    if (isPlaying) {
      // Start animation
      animationRef.current = setInterval(() => {
        setSliderValue((prevValue) => {
          // If at the end of the timeline, stop playing and stay at the end
          if (prevValue >= timeRange.length - 1) {
            // Schedule state update to stop playing
            setTimeout(() => setIsPlaying(false), 0);
            // Return current value (end of timeline)
            return prevValue;
          }
          // Otherwise advance to next frame
          return prevValue + 1;
        });
      }, animationSpeed);
    } else {
      // Stop animation
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    }
    
    // Cleanup interval on unmount or when isPlaying changes
    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [isPlaying, animationSpeed, timeRange.length]);

  // Update hovered feature when slider changes (during animation or manual slider move)
  // This ensures the forecast value in the popup stays current
  useEffect(() => {
    if (hoveredFeature && assetGeoJSON && assetGeoJSON.features) {
      const featureId = hoveredFeature.feature.properties.id;
      const updatedFeature = assetGeoJSON.features.find(f => f.properties.id === featureId);

      if (updatedFeature) {
        // Update the hovered feature with fresh forecast data
        setHoveredFeature({
          ...hoveredFeature,
          feature: updatedFeature
        });
      }
    }
  }, [sliderValue, assetGeoJSON]);

  // Format time for display
  const formatSliderTime = () => {

    if (!timeRange.length || sliderValue >= timeRange.length) {
      return `Loading... ${timeRange.length} `;
    }

    const time = timeRange[sliderValue];
    
    // Check if we're using the new processed model format where timestamp is stored in a timestamp property
    if (time && typeof time === 'object' && time.timestamp) {
      // For processed model format
      const timestamp = time.timestamp;
      
      // Make sure it's a valid date
      if (!(timestamp instanceof Date) || isNaN(timestamp.getTime())) {
        console.error("Invalid timestamp in timeRange:", timestamp);
        return 'Invalid Date';
      }
      
      if (localTimeBool) {
        // Format in local time with timezone indication
        const timeString = timestamp.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
  
        // Add the timezone
        const timezoneString = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
          .formatToParts(timestamp)
          .find(part => part.type === 'timeZoneName')?.value || '';
  
        return `${timeString} ${timezoneString}`;
      } else {
        // Format in UTC
        return timestamp.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'UTC'
        }) + ' UTC';
      }
    } else {
      // For standard format (time is a Date object)
      if (!(time instanceof Date) || isNaN(time.getTime())) {
        console.error("Invalid time in timeRange:", time);
        return 'Invalid Date';
      }
      
      if (localTimeBool) {
        // Format in local time with timezone indication
        const timeString = time.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
  
        // Add the timezone
        const timezoneString = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
          .formatToParts(time)
          .find(part => part.type === 'timeZoneName')?.value || '';
  
        return `${timeString} ${timezoneString}`;
      } else {
        // Format in UTC
        return time.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'UTC'
        }) + ' UTC';
      }
    }
  };

  // Define available forecast variations based on forecast type
  const forecastVariations = React.useMemo(() => {
    // Start with the base variations from constants
    const variations = { ...FORECAST_VARIATIONS };
    
    // Add processed models variations if available
    if (processedModelData) {
      Object.keys(processedModelData).forEach(modelKey => {
        const modelData = processedModelData[modelKey];
        if (modelData.models) {
          // Create variations for this processed model
          variations[`processed-${modelKey}`] = Object.keys(modelData.models).map(variationKey => ({
            name: modelData.models[variationKey].variation,
            key: variationKey
          }));
        }
      });
    }
    
    return variations;
  }, [processedModelData]);
  

  // Helper function to update time range based on current selections
  // Helper function to filter forecast types based on assetGroup.displayedChannels
  // The map view's keys ("wind10m") differ from the saved channel keys ("Wind10"), so the util maps between them
  const filterForecastTypes = (forecastTypeKey) =>
    isMapViewForecastDisplayed(assetGroup?.displayedChannels, forecastTypeKey);

  // Function to find and select the 'average' channel option
  const selectDefaultAverageChannel = (channelOptions) => {
    // Look for 'average' in the option value or label
    const averageOption = channelOptions.find(option => {

      return Boolean(option.value.toLowerCase().includes('hourly') ||
      option.value.toLowerCase().includes('average') ||
      option.value.toLowerCase().includes('mean') ||
      option.value.toLowerCase().includes('control'))
    }
    );
    if (averageOption) {
      // setForecastChannel(averageOption.value);
      return averageOption.value;
    }

    // If no 'average' option found, return null
    return null;
  };

  // Function to update channel options based on forecast type
  const updateChannelOptions = (type, pModelData) => {
    // Start with an empty array
    let options = [];

    // For processed models
    if (type.startsWith('processed-')) {
      const modelKey = type.split('-')[1];

      // Check if we have processed model data
      if (pModelData && Object.keys(pModelData).length > 0) {
        // Extract all variation + percentile combinations across all assets
        options = extractProcessedModelChannelsWithMax(modelKey, pModelData);
      } else {
        console.warn("No processed model data available for updating channel options");
      }

      setChannelOptions(options)
      const defaultChannelValue = selectDefaultAverageChannel(options);

      // setForecastChannel(defaultChannelValue)
      const syntheticEvent = {target: { value: defaultChannelValue }}
      handleForecastChannelChange(syntheticEvent, options)
      // CRAIG HERE
      return; // Early return for processed models (no auto-selection for processed models)
    }

    // For standard forecast types CRAIG MOVE THIS TO
    switch (type) {
      case 'precipitation':
        options = PRECIPITATION_CHANNEL_OPTIONS
        break;
      case 'wind10m':
        options = WIND_10_CHANNEL_OPTIONS
        break
      case 'wind80m':
        options = WIND_80_CHANNEL_OPTIONS
        break;
      case 'temperature':
        options = TEMPERATURE_CHANNEL_OPTIONS
        break;
      case 'humidity':
        options = HUMIDITY_CHANNEL_OPTIONS;
        break;
      case 'solarS':
        options = SOLAR_SHORT_CHANNEL_OPTIONS;
        break;
      case 'solarL':
        options = SOLAR_LONG_CHANNEL_OPTIONS;
        break;
      case 'soilMoisture':
        options = SOIL_MOISTURE_CHANNEL_OPTIONS;
        break;
      case 'snowWater':
        options = SNOW_WATER_CHANNEL_OPTIONS;
        break;
      case 'windPower':
        options = WIND_POWER_CHANNEL_OPTIONS;
        break;
      default:
        options = [];
    }

    setChannelOptions(options);

    // Auto-select 'average' channel for non-processed models only when forecast type changes
    if (options.length > 0) {
      const defaultChannelValue = selectDefaultAverageChannel(options);
      setForecastChannel(defaultChannelValue)
      // const sample = { target: { value: defaultChannelValue }}
      updateVariationOptions(type, defaultChannelValue)
    }
  };
  
  // Function to fetch forecast data on demand
  const getProcessedModelS3 = async (processedModelsList) => {
    try {
      setProcessedModelsLoading(true);
      
      // Create an object to store processed model data by model type (routeShort)
      const finalProcessedModelData = {};
      
      // Process each model in the list - these should all have latestFile property
      const promises = processedModelsList.map(async (processedModel) => {
        const { assetId, routeShort, latestFile } = processedModel;
        
        if (!latestFile) {
          console.warn(`No latestFile found for model ${routeShort} (Asset ID: ${assetId})`);
          return null;
        }
        
          try {
          // Fetch the processed model file directly using latestFile
            const downloadResult = await downloadData({
            path: latestFile,
            }).result;
            
            const jsonData = await downloadResult.body.text();
            const processedModelResult = JSON.parse(jsonData);
          
          // Add assetId to the processed model result for reference
          processedModelResult.assetId = assetId;
          
          return { 
            routeShort, 
            processedModelResult,
            assetId 
          };
          } catch (error) {
          console.error(`Error fetching processed model for ${routeShort} (Asset ID: ${assetId}):`, error);
          return null;
          }
        });
        
      // Wait for all fetches to complete
      const results = await Promise.all(promises);
      
      // Process results and build the final data structure
      results.forEach(result => {
        if (result) {
          const { routeShort, processedModelResult, assetId } = result;
          
        
          // Initialize the entry for this route short if it doesn't exist
          if (!finalProcessedModelData[assetId]) {
            // Ensure the processedModelResult has a models field
            if (!processedModelResult.models) {
              processedModelResult.models = {};
            }
            
            // Add the model to the asset's models
            if (!processedModelResult.models[routeShort]) {
              // Structure expected by extractProcessedModelChannelsWithMax
              processedModelResult.models[routeShort] = {
                variation: routeShort,
                data: processedModelResult.data || {}
              };
            }
            
            finalProcessedModelData[assetId] = {
              models: processedModelResult.models,
              assetId
            };
          }
        }
      });
      
      // Update processed model data
      setProcessedModelData(finalProcessedModelData);
      setProcessedModelsLoading(false);
      
      // After loading the data, update channel options if this is a processed model forecast
      if (forecastType.startsWith('processed-')) {
        // Update channel options based on the new data
        updateChannelOptions(forecastType, finalProcessedModelData);
        
      }
    } catch (error) {
      console.error("Error loading processed models:", error);
      setProcessedModelsLoading(false);
    }
  };

  // Function to fetch directory.json files and update processed model details
  const fetchDirectoryJsonFiles = async (processedModels) => {
    if (!processedModels || processedModels.length === 0) return [];
    try {
      // Use an object to store unique directory.json URLs with their models
      const uniqueDirectoryUrls = {};
      
      // Map each processed model to its directory.json URL
      processedModels.forEach(model => {
        if (model.routeLong && model.routeShort) {
          // Remove the routeShort and its surrounding slashes from routeLong
          // Format: path/to/assets/assetId/processedModels/routeShort/
          // Need to remove the routeShort and trailing slash, but keep the path up to processedModels/
          // Example: "organizationprivate/bf/assets/assetId/processedModels/turbineflowsv2/" 
          // Becomes: "organizationprivate/bf/assets/assetId/processedModels/directory.json"
          const basePath = withTrailingSlash(model.routeLong).replace(`${model.routeShort}/`, '');
          const directoryJsonUrl = `${basePath}directory.json`;
          
          // Store in object with URL as key and models array as value
          if (!uniqueDirectoryUrls[directoryJsonUrl]) {
            uniqueDirectoryUrls[directoryJsonUrl] = [];
          }
          uniqueDirectoryUrls[directoryJsonUrl].push(model);
        }
      });
      
      // Fetch all unique directory.json files
      const fetchPromises = Object.entries(uniqueDirectoryUrls).map(async ([url, models]) => {
        try {
          const { url: signedUrl } = await getUrl({ path: url });
          const response = await fetch(signedUrl.toString(), { cache: 'no-store' });
          
          if (!response.ok) {
            console.error(`Failed to fetch directory.json: ${response.status}`);
            return null;
          }
          
          const directoryData = await response.json();
          const basePath = url.replace('directory.json', '');
          return { directoryData, models, basePath };
        } catch (error) {
          console.error(`Error fetching directory.json for ${url}:`, error);
          return null;
        }
      });
      
      const results = await Promise.all(fetchPromises);
      
      // Filter out failed requests and update processed model details
      const updatedProcessedModels = processedModels.map(model => {
        // Find matching directory data for this model by finding the result that includes this model
        const matchingResult = results.find(result => 
          result && result.models.some(m => 
            m.routeShort === model.routeShort && m.assetId === model.assetId
          )
        );
        
        if (matchingResult) {
          const { directoryData, basePath } = matchingResult;
          // Get the latest file for this route short
          const latestFile = directoryData[model.routeShort];
          
          if (latestFile) {
            // Add latestFile to the model details
            // Format the path as: basePath/routeShort/latestFile
            // Example: "organizationprivate/brookfieldrenewable/assets/assetId/processedModels/turbineflowsv2/2024-05-29-00-forecast-file.json"
            return {
              ...model,
              latestFile: `${basePath}${model.routeShort}/${latestFile}`
            };
          }
        }
        
        return model;
      });
      
      // Update state with the enhanced model details
      setProcessedModelDetails(updatedProcessedModels);
      
      // Return the updated models for use in chained promises
      return updatedProcessedModels;
    } catch (error) {
      console.error("Error fetching directory.json files:", error);
      return processedModels; // Return original models in case of error
    }
  };

  
  // Function to update variation options based on forecast type and channel
  const updateVariationOptions = (type, channel) => {

    let options = [];
    let preferredVariation = ''
    // For processed models, don't show variation dropdown
    if (type.startsWith('processed-')) {
      // Return empty options array for processed models
      return setVariationOptions([]);
    } else if (type === 'precipitation') {
      options = [
          { value: 'Hourly', label: 'Hourly' },
          { value: 'Cumulative', label: 'Cumulative' }
        ];
      preferredVariation = 'Hourly'
    } else if (type === 'wind80m' || type === 'wind10m') {
      options = [
          { value: 'Speed', label: 'Speed' },
          { value: 'Direction', label: 'Direction' }
        ];
      preferredVariation = 'Speed'
    } 
    else {
      preferredVariation = '';
    }

    setVariationOptions(options);
    setForecastVariation(preferredVariation)
  };
  
  // Craig This needs a refactor
  //CRAIG THIS NEXT
  const updateTimeRange = (type, variation, channel) => {
    if (Object.keys(forecastData).length === 0) return;
    const firstAssetId = Object.keys(forecastData)[0];
    const thefwData = forecastData[firstAssetId]?.thefw;
    const wind80Data = forecastData[firstAssetId]?.wind80;
    const ohhfcData = forecastData[firstAssetId]?.ohhfc;
    const graphcastData = forecastData[firstAssetId]?.graphcast;
    
    const isProcessedModel = type.startsWith('processed-');
    
    // Get the appropriate time range data based on forecast type, variation, and channel
    let timeData = null;
    
    // Reset the slider value when switching between forecast types
    // since the time scales may be different
    if (timeRange.length > 0 && sliderValue > 0) {
      setSliderValue(0);
    }
    if (isProcessedModel) {
      try {
        const modelKey = type.split('-')[1]; // Extract model key from type (e.g., 'processed-turbineflowsv2')
        
        // Expecting channel to be in format "Hourly Gen Flow mean" or "Hourly Gen Flow 0.5"
        // Parse the channel to get variation and percentile
        const [variation, percentile] = channel.split(' ').reduce((acc, part, index, array) => {
          if (index === array.length - 1) {
            // Last part is the percentile
            acc[1] = part;
          } else {
            // Combine all parts except the last one as the variation
            acc[0] = acc[0] ? `${acc[0]} ${part}` : part;
          }
          return acc;
        }, ['', '']);
        
        // Create an object to store data for each asset instead of using Map constructor
        const assetDataMap = {};
        
        // Process each asset's data
        Object.entries(processedModelData).forEach(([assetId, assetData]) => {
          if (!assetData.models) return;
          
          // Find a model that matches the variation
          const modelEntry = Object.entries(assetData.models).find(([modelName, modelData]) => 
            modelData.variation === variation
          );
          
          if (!modelEntry) return;
          
          const [modelName, modelData] = modelEntry;
          
          // Get the data for this model
          if (modelData.data) {
            const formattedData = [];
            
            // Process each timestamp
            Object.entries(modelData.data).forEach(([timestamp, dataPoints]) => {
              // Check if this timestamp has the selected percentile
              if (dataPoints[percentile] !== undefined) {
                
                // Parse the timestamp string properly
                let dateObj;
                try {
                  // Handle various timestamp formats
                  if (typeof timestamp === 'string') {
                    // Check for format like "2024-08-28 00:00:00"
                    if (timestamp.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
                      // Replace space with T and add Z to make it ISO format
                      const isoTimestamp = timestamp.replace(' ', 'T') + 'Z';
                      dateObj = new Date(isoTimestamp);
                    }
                    // If timestamp ends with Z, it's UTC
                    else if (timestamp.endsWith('Z')) {
                      dateObj = new Date(timestamp);
                    } else {
                      // Try to parse as ISO format
                      dateObj = new Date(timestamp);
                      
                      // If parsing failed, try adding Z to make it UTC
                      if (isNaN(dateObj.getTime())) {
                        dateObj = new Date(`${timestamp}Z`);
                      }
                    }
                  } else {
                    // Already a date or number
                    dateObj = new Date(timestamp);
                  }
                  
                  // Check if the date is valid
                  if (isNaN(dateObj.getTime())) {
                    console.warn(`Invalid date after parsing: ${timestamp} for asset ${assetId}`);
                    return; // Skip invalid dates
                  }
                } catch (e) {
                  console.error(`Error parsing date: ${timestamp}`, e);
                  return; // Skip on error
                }
                
                formattedData.push({
                  x: dateObj,
                  y: dataPoints[percentile],
                  assetId: assetId
                });
              }
            });
            
            // Store the formatted data for this asset
            if (formattedData.length > 0) {
              assetDataMap[assetId] = formattedData;
            }
          }
        });
        
        // Combine all asset data into a single array for time slider
        const allFormattedData = [];
        
        // Get all unique timestamps across all assets
        const allTimestamps = new Set();
        Object.values(assetDataMap).forEach(data => {
          data.forEach(point => {
            allTimestamps.add(point.x.getTime());
          });
        });
        
        // Sort timestamps chronologically
        const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => a - b);
        
        // Create formatted data objects for each timestamp
        sortedTimestamps.forEach(timestamp => {
          const timestampDate = new Date(timestamp);
          
          // Verify the date is valid
          if (isNaN(timestampDate.getTime())) {
            console.error(`Invalid timestamp after sorting: ${timestamp}`);
            return; // Skip this timestamp
          }
          
          const dataForTimestamp = {
            timestamp: timestampDate,
            assets: {}
          };
          
          // Add data for each asset at this timestamp
          Object.entries(assetDataMap).forEach(([assetId, data]) => {
            const pointForTimestamp = data.find(point => point.x.getTime() === timestamp);
            if (pointForTimestamp) {
              dataForTimestamp.assets[assetId] = pointForTimestamp.y;
            } else {
              // Use N/A for assets that don't have data for this timestamp
              dataForTimestamp.assets[assetId] = "N/A";
            }
          });
          
          allFormattedData.push(dataForTimestamp);
        });
        
        // Set the time data for the slider
        if (allFormattedData.length > 0) {
          // Make sure all timestamps are valid before setting the data
          const validData = allFormattedData.filter(item => {
            const isValid = item.timestamp && !isNaN(item.timestamp.getTime());
            if (!isValid) {
              console.error("Invalid timestamp found in final data:", item);
            }
            return isValid;
          });
          
          timeData = validData;
          setTimeRange(validData);
          setSliderValue(0); // Reset to first timestamp
        }
      } catch (error) {
        console.error('Error handling processed model time data:', error);
      }
    }
    
    if (timeData) {
      const times = timeData.map(item => new Date(item.x));
      setTimeRange(times);
      setSliderValue(0); // Reset to the beginning
    } else {
      console.warn(`Could not find any valid time data for ${type}, variation: ${variation}, channel: ${channel}`);
    }
  };

  // Handle forecast type change
  const handleForecastTypeChange = (event) => {
    const newType = event.target.value;

    setTimeRange([]);
    setDataFetched(false);
    
    // Check if this is a processed model type
    const isProcessedModel = newType.startsWith('processed-');
  
    // For processed models, check if we need to load the data
    // processedModelDetails
    if (isProcessedModel && !processedModelData || isProcessedModel && newType !== forecastType) {
      // Find processed models that match the selected forecast type
      const processedModelsForecastTypeList = processedModelDetails.filter(pm => Boolean(`processed-${pm.routeShort}` === newType))
      
      // Check if we have models with latestFile (indicating directory.json was fetched successfully)
      const modelsWithLatestFile = processedModelsForecastTypeList.filter(pm => pm.latestFile);
      
      //Commenting out, don't fetch yet
      if (modelsWithLatestFile.length > 0) {
        // We have models with latest file info, fetch the processed model data
        getProcessedModelS3(modelsWithLatestFile);
      } else if (processedModelsForecastTypeList.length > 0) {
        // We have models but no latest file info yet, trigger directory fetch first
        fetchDirectoryJsonFiles(processedModelsForecastTypeList).then((updatedModels) => {
          // After fetching directory info, try to get models with latest file again
          const updatedModelsWithLatestFile = updatedModels.filter(pm => pm.latestFile);
          
          if (updatedModelsWithLatestFile.length > 0) {
            getProcessedModelS3(updatedModelsWithLatestFile);
          }
        });
      }
    }
    
    // Update the forecast type
    setForecastType(newType);
    
    // Update channel options based on the new forecast type
    // If this is a processed model, pass the current processedModelData (might be updated later)
    updateChannelOptions(newType, isProcessedModel ? processedModelData : null);
    
    // For processed models, trigger data loading immediately (without waiting for channel selection)
    if (isProcessedModel) {
      // Get processed models that match this forecast type (same logic as above, just moved here)
      const processedModelList = processedModelDetails.filter(pm => `processed-${pm.routeShort}` === newType);
      
      if (processedModelList.length > 0) {
        // Instead of waiting for the useEffect, directly trigger loading for processed models
        setLoading(true);
        
        // If we have models with latest file info, fetch the processed model data right away
        const modelsWithLatestFile = processedModelList.filter(pm => pm.latestFile);
        if (modelsWithLatestFile.length > 0) {
          getProcessedModelS3(modelsWithLatestFile);
        }
        // Otherwise the directory fetch logic above will handle it
      }
    }
  };
  
  // Handle forecast channel change
  const handleForecastChannelChange = (event, passedOptions=[]) => {
    const newChannel = event.target.value;
    
    let availableOptions = channelOptions
    if(passedOptions && passedOptions?.length > 0) {
      availableOptions = passedOptions
    }

    // Set channel and reset variation
    setForecastChannel(newChannel);
    setForecastVariation('');
    
    // Check if this is a processed model channel
    const isProcessedModel = forecastType.startsWith('processed-');
    
    if (isProcessedModel) {
      // For processed model data, we need to extract the time range
      const modelKey = forecastType.split('-')[1]; // Extract model key
      
      // Extract timeRange using the utility function
      const selectedEventChannel = availableOptions.find(option=>option.value === newChannel)
      const extractedTimeRange = extractTimeRangeFromProcessedModel(processedModelData, selectedEventChannel);
      
      if (extractedTimeRange && extractedTimeRange.length > 0) {
        // Update the time range for the slider
        setTimeRange(extractedTimeRange);
        // Reset slider to beginning
        setSliderValue(0);
        
      } else {
        console.warn(`No time range data available for ${modelKey}, channel: ${newChannel}`);
      }
    } else {
      updateVariationOptions(forecastType, newChannel);
    }
    
    // Fetch data for the selected forecast type and channel
    // fetchForecastData(forecastType, newChannel);
  };
  
  // Handle forecast variation change
  const handleForecastVariationChange = (event) => {
    const newVariation = event.target.value;
    setForecastVariation(newVariation);
    
    // Update time range with the new variation
    updateTimeRange(forecastType, newVariation, forecastChannel);
  };

  // Toggle for mobile controls expansion
  const toggleControls = () => {
    setControlsExpanded(!controlsExpanded);
  };
  
  // Toggle visibility of the controls panel
  const toggleControlsVisibility = () => {
    setControlsVisible(!controlsVisible);
  };
  
  // Toggle mobile menu drawer
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Monitor processed model data changes and update channel options if needed
  useEffect(() => {
    // When processed model data changes, update channel options
    if (processedModelData && forecastType && forecastType.startsWith('processed-')) {
      // Update the channel options with the new data, passing the processedModelData
      updateChannelOptions(forecastType, processedModelData);
      
      // Set loading to false when processed model data is loaded
      setLoading(false);
    }
  }, [processedModelData, forecastType]);


  const selectedVariationChannel = channelOptions.find(channelOptn => channelOptn.value === forecastChannel);

  return (
    <div className={`w-full h-full bg-white ${!isMobile ? 'overflow-hidden border border-gray-200 rounded-md' : ''}`} style={{margin: 0, padding: 0, overflow: 'hidden'}}>
      {/* Add style element for cursor control */}
      <style>{mapCursorStyle}</style>
      <div className={`w-full h-full ${isMobile ? '' : 'relative'}`} style={isMobile ? {display: 'flex', flexDirection: 'column', margin: 0, padding: 0} : {}}>

        {/* Mobile Drawer for Settings - Only for mobile */}
        {isMobile && !isEmbedded && (
          <MobileDrawer
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            forecastType={forecastType}
            handleForecastTypeChange={handleForecastTypeChange}
            showWindPowerOption={showWindPowerOption}
            forecastChannel={forecastChannel}
            handleForecastChannelChange={handleForecastChannelChange}
            channelOptions={channelOptions}
            forecastVariation={forecastVariation}
            handleForecastVariationChange={handleForecastVariationChange}
            variationOptions={variationOptions}
            showAllTooltips={showAllTooltips}
            setShowAllTooltips={setShowAllTooltips}
            localTimeBool={localTimeBool}
            setUseLocalTime={setUseLocalTime}
            processedModelData={processedModelData}
            dataFetched={dataFetched}
            assetItems={assetItems}
            assetGroup={assetGroup}
            filterForecastTypes={filterForecastTypes}
          />
        )}

        {/* Mobile Controls Panel */}
        {isMobile && !isEmbedded && (
          <MobileControlPanel
            formatSliderTime={formatSliderTime}
            timeRange={timeRange}
            loading={loading}
            sliderValue={sliderValue}
            handleSliderChange={handleSliderChange}
            isPlaying={isPlaying}
            togglePlayAnimation={togglePlayAnimation}
            restartSlider={restartSlider}
            stepForward={stepForward}
            toggleMobileMenu={toggleMobileMenu}
          />
        )}

        {/* Desktop Controls Panel */}
        {!isEmbedded && (
          <ForecastControls
            isMobile={isMobile}
            showAllTooltips={showAllTooltips}
            setShowAllTooltips={setShowAllTooltips}
            forecastType={forecastType}
            handleForecastTypeChange={handleForecastTypeChange}
            showWindPowerOption={showWindPowerOption}
            forecastChannel={forecastChannel}
            handleForecastChannelChange={handleForecastChannelChange}
            channelOptions={channelOptions}
            forecastVariation={forecastVariation}
            handleForecastVariationChange={handleForecastVariationChange}
            variationOptions={variationOptions}
            timeRange={timeRange}
            loading={loading}
            sliderValue={sliderValue}
            handleSliderChange={handleSliderChange}
            formatSliderTime={formatSliderTime}
            isPlaying={isPlaying}
            togglePlayAnimation={togglePlayAnimation}
            restartSlider={restartSlider}
            stepForward={stepForward}
            processedModelDetails={processedModelDetails}
            dataFetched={dataFetched}
            assetItems={assetItems}
            assetGroup={assetGroup}
            filterForecastTypes={filterForecastTypes}
          />
        )}

        {mapReady && <Map
          ref={mapRef}
          mapboxAccessToken={MAPBOX_TOKEN}
          {...viewState}
          onMove={(evt) => setViewState(evt.viewState)}
          style={isMobile ?
            {flexGrow: 1, width: '100%'} :
            {width: "100%", height: "calc(100%)"}
          }
          cursor={hoveredFeature ? 'pointer' : 'default'}
          mapStyle={mapStyle}
          onLoad={handleMapLoad}
          onClick={handleAssetClick}
          onContextMenu={handleMapContextMenu}
          onMouseMove={handleAssetHover}
          onMouseLeave={handleAssetLeave}
          onTouchStart={handleLongPressStart}
          onTouchEnd={handleLongPressEnd}
          onTouchMove={handleLongPressEnd}
          interactiveLayerIds={interactiveLayerIds}
        >
          {assetGeoJSON && (
            <Source id="assets-source" type="geojson" data={assetGeoJSON}>
              {/* Polygons - Fill */}
              <Layer
                id="assets-fill"
                type="fill"
                paint={{
                  "fill-color": ["get", "color"],
                  "fill-opacity": 0.6,
                }}
                filter={[
                  "all",
                  ["in", "$type", "Polygon"]
                ]}
              />
              
              {/* Lines and Polygon outlines */}
              <Layer
                id="assets-line"
                type="line"
                paint={{
                  "line-color": ["get", "color"],
                  "line-width": 5,
                  "line-opacity": 0.85,
                }}
                filter={[
                  "all",
                  ["in", "$type", "LineString", "Polygon"]
                ]}
              />
              
              {/* Points — default circles for assets without a custom icon */}
              <Layer
                id="assets-points"
                type="circle"
                paint={{
                  "circle-radius": 6,
                  "circle-color": ["get", "color"],
                  "circle-stroke-width": 2,
                  "circle-stroke-color": "#000000",
                }}
                filter={[
                  "all",
                  ["==", "$type", "Point"],
                  ["!has", "icon"]
                ]}
              />

              {/* Custom icon fill — dynamic color from forecast data */}
              {iconsVersion > 0 && (
                <Layer
                  key={`assets-icons-fill-${iconsVersion}`}
                  id="assets-icons-fill"
                  type="symbol"
                  layout={{
                    "icon-image": getIconImageExpression("fill"),
                    "icon-size": 0.8,
                    "icon-allow-overlap": true,
                  }}
                  paint={{
                    "icon-color": ["get", "color"],
                  }}
                  filter={[
                    "all",
                    ["==", "$type", "Point"],
                    ["has", "icon"]
                  ]}
                />
              )}

              {/* Custom icon stroke — fixed dark outline on top */}
              {iconsVersion > 0 && (
                <Layer
                  key={`assets-icons-stroke-${iconsVersion}`}
                  id="assets-icons-stroke"
                  type="symbol"
                  layout={{
                    "icon-image": getIconImageExpression("stroke"),
                    "icon-size": 0.8,
                    "icon-allow-overlap": true,
                  }}
                  paint={{
                    "icon-color": "#1a1a1a",
                  }}
                  filter={[
                    "all",
                    ["==", "$type", "Point"],
                    ["has", "icon"]
                  ]}
                />
              )}

              {/* Invisible larger hover area for easier interaction */}
              <Layer
                id="assets-hover-area"
                type="circle"
                paint={{
                  "circle-radius": 12, // Larger circle for easier hovering
                  "circle-opacity": 0, // Completely transparent
                  "circle-stroke-width": 0 // No stroke
                }}
                filter={[
                  "all",
                  ["==", "$type", "Point"]
                ]}
              />
              
              {/* Asset Labels - Hidden by default, MapPopups component handles all label rendering */}
              <Layer
                id="assets-labels"
                type="symbol"
                layout={{
                  "visibility": "none" // Always hidden - MapPopups component renders all labels
                }}
                paint={{
                  "text-color": "#333",
                  "text-halo-color": "#fff",
                  "text-halo-width": 1,
                }}
              />
            </Source>
          )}
          
          {/* Hover Popup */}
          <MapPopups 
            hoveredFeature={hoveredFeature}
            showAllTooltips={showAllTooltips}
            forecastType={forecastType}
            assetGeoJSON={assetGeoJSON}
          />

          {/* Map Style Toggle */}
          <MapStyleToggle 
            mapStyle={mapStyle} 
            onStyleChange={setMapStyle} 
          />

          {/* Legend */}
          {!isEmbedded && (
            <ForecastLegend forecastType={forecastType} maxWindPower={maxWindPower} selectedVariationChannel={selectedVariationChannel ?? 100} />
          )}
        </Map>}

        {/* Loading indicator */}
        {loading && <LoadingOverlay />}
      </div>
    </div>
  );
};

export default AssetMapView;
